import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

import { loginRequest } from '@core/auth/msal.config';
import { LoginScreen } from '@app/LoginScreen';
import { BootScreen } from '@app/BootScreen';

/**
 * The route guard: it wraps the whole shell and gates it on an account.
 *
 * Its job is to start a redirect sign-in the moment it finds no signed-in
 * account - no prompt, no button. That is what makes single sign-on feel like
 * single sign-on: a user who already has a tenant session lands on the portal
 * and is simply in it, because the redirect to Entra comes back immediately
 * with an account. Rendering the sign-in screen instead whenever
 * `isAuthenticated` is false would put a click on "Login as Internal user" in
 * front of every visit before any of the SSO machinery ran.
 *
 * Two things keep the automatic redirect from becoming a trap:
 *
 *   - `inProgress`. MSAL settles a redirect asynchronously, and firing a second
 *     `loginRedirect` while the first interaction is still in flight throws
 *     `interaction_in_progress` and strands the user on a blank page.
 *   - The attempt marker below. If the redirect comes back still unauthenticated
 *     - consent declined, a conditional-access block, an account that cannot
 *     reach this tenant - retrying would bounce the browser between the portal
 *     and Entra indefinitely. One automatic attempt per tab; after that the
 *     manual screen is shown so there is something to read and something to
 *     click.
 */

/*
 * `sessionStorage`, not `localStorage`: the marker is about this tab's current
 * visit, not a durable preference. A new tab should get its own automatic
 * attempt, and closing the browser should forget a failed one.
 */
const AUTO_SIGN_IN_MARKER = 'autoSignInAttempted';

/** Set by the shell just before `logoutRedirect`, so that signing out lands on
    the sign-in screen instead of being immediately signed back in. Without it
    an automatic guard makes sign-out unreachable: the logout redirect returns
    to a shell that sees no account and sends the user straight back to Entra. */
export const SIGNED_OUT_MARKER = 'signedOut';

function marked(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === 'true';
  } catch {
    // Storage disabled (Safari private mode, hardened enterprise policy). The
    // guard degrades to the manual screen rather than to a redirect loop.
    return true;
  }
}

function mark(key: string, value: boolean): void {
  try {
    if (value) {
      sessionStorage.setItem(key, 'true');
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    /* nothing to record; `marked` fails closed */
  }
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  // Read once, at mount: this is the state the tab arrived in. Reading it
  // during render on every pass would flip the branch under us as soon as the
  // effect below writes the marker.
  const [autoSignInSpent, setAutoSignInSpent] = useState(
    () => marked(AUTO_SIGN_IN_MARKER) || marked(SIGNED_OUT_MARKER)
  );

  /** The manual path, and the reset: an explicit click is a fresh mandate to
      redirect, so it clears both markers before handing back to MSAL. */
  const signIn = useCallback(() => {
    mark(SIGNED_OUT_MARKER, false);
    mark(AUTO_SIGN_IN_MARKER, true);
    setAutoSignInSpent(true);
    void instance.loginRedirect(loginRequest);
  }, [instance]);

  // A completed sign-in retires the markers, so a later sign-out inside the
  // same tab starts from a clean slate.
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    mark(AUTO_SIGN_IN_MARKER, false);
    mark(SIGNED_OUT_MARKER, false);
  }, [isAuthenticated]);

  /*
   * A second `loginRedirect` before the first has navigated throws
   * `interaction_in_progress`. The state flag alone does not prevent that under
   * StrictMode, which runs mount effects twice against the same state snapshot,
   * so the ref is what actually makes the call once per mount.
   */
  const redirectStarted = useRef(false);

  useEffect(() => {
    if (isAuthenticated || autoSignInSpent || redirectStarted.current) {
      return;
    }

    if (inProgress !== InteractionStatus.None) {
      return;
    }

    // Recorded before the call, not after: `loginRedirect` navigates away, so
    // anything queued behind it may never run.
    redirectStarted.current = true;
    mark(AUTO_SIGN_IN_MARKER, true);
    setAutoSignInSpent(true);

    void instance.loginRedirect(loginRequest).catch(() => {
      // The redirect never left - MSAL refused it, or the browser blocked the
      // navigation. `autoSignInSpent` is already set, so the render below falls
      // through to the manual screen: something to act on beats a spinner that
      // never resolves.
      redirectStarted.current = false;
    });
  }, [isAuthenticated, autoSignInSpent, inProgress, instance]);

  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Only once the automatic attempt is spent, so the ordinary SSO path never
  // flashes a sign-in screen on its way to Entra.
  if (autoSignInSpent && inProgress === InteractionStatus.None) {
    return <LoginScreen onLogin={signIn} />;
  }

  // Sign-in is a full page navigation, so this is on screen for the moment
  // before the browser leaves - the same stepped screen the static copy in
  // index.html showed while the bundle loaded, so nothing jumps.
  return <BootScreen />;
}

export default RequireAuth;
